import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

// ============================================================================
// Equipment type options (14 enum values from schema)
// ============================================================================
const EQUIPMENT_TYPE_OPTIONS = [
  { value: "forklift", label: "Forklift" },
  { value: "access_equipment", label: "Access Equipment" },
  { value: "agricultural_tractor", label: "Agricultural Tractor" },
  { value: "agricultural_harvester", label: "Agricultural Harvester" },
  { value: "agricultural_other", label: "Agricultural Other" },
  { value: "construction_excavator", label: "Construction Excavator" },
  { value: "construction_loader", label: "Construction Loader" },
  { value: "construction_crane", label: "Construction Crane" },
  { value: "construction_other", label: "Construction Other" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "medical_equipment", label: "Medical Equipment" },
  { value: "it_equipment", label: "IT Equipment" },
  { value: "office_equipment", label: "Office Equipment" },
  { value: "other_equipment", label: "Other Equipment" },
];

// Condition options (CHECK constraint values)
const CONDITION_OPTIONS = [
  { value: "new", label: "New" },
  { value: "excellent", label: "Excellent" },
  { value: "good", label: "Good" },
  { value: "fair", label: "Fair" },
  { value: "poor", label: "Poor" },
];

// ============================================================================
// Form Schema
// ============================================================================
const equipmentFormSchema = z.object({
  equipmentType: z.string().min(1, "Equipment type is required"),
  serialNumber: z.string().optional(),
  assetTag: z.string().optional(),
  make: z.string().optional(),
  model: z.string().optional(),
  yearManufactured: z.coerce.number().int().min(1900).max(2100).optional().nullable(),
  description: z.string().optional(),
  condition: z.string().optional(),
  locationAddress: z.string().optional(),
  isMobile: z.boolean(),
  hoursUsed: z.coerce.number().int().min(0).optional().nullable(),
  weightKg: z.string().optional(),
  capacity: z.string().optional(),
});

type EquipmentFormData = z.infer<typeof equipmentFormSchema>;

const defaultValues: EquipmentFormData = {
  equipmentType: "",
  serialNumber: "",
  assetTag: "",
  make: "",
  model: "",
  yearManufactured: null,
  description: "",
  condition: "none",
  locationAddress: "",
  isMobile: true,
  hoursUsed: null,
  weightKg: "",
  capacity: "",
};

// ============================================================================
// Props
// ============================================================================
interface EquipmentFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  securityId: string;
  existingEquipment?: any;
}

// ============================================================================
// Component
// ============================================================================
export function EquipmentFormModal({
  open,
  onOpenChange,
  securityId,
  existingEquipment,
}: EquipmentFormModalProps) {
  const isEdit = !!existingEquipment;
  const { toast } = useToast();

  const form = useForm<EquipmentFormData>({
    resolver: zodResolver(equipmentFormSchema),
    defaultValues,
  });

  // Populate form when opening in edit mode
  useEffect(() => {
    if (open) {
      if (existingEquipment) {
        form.reset({
          equipmentType: existingEquipment.equipmentType || "",
          serialNumber: existingEquipment.serialNumber || "",
          assetTag: existingEquipment.assetTag || "",
          make: existingEquipment.make || "",
          model: existingEquipment.model || "",
          yearManufactured: existingEquipment.yearManufactured ?? null,
          description: existingEquipment.description || "",
          condition: existingEquipment.condition || "none",
          locationAddress: existingEquipment.locationAddress || "",
          isMobile: existingEquipment.isMobile ?? true,
          hoursUsed: existingEquipment.hoursUsed ?? null,
          weightKg: existingEquipment.weightKg?.toString() || "",
          capacity: existingEquipment.capacity || "",
        });
      } else {
        form.reset(defaultValues);
      }
    }
  }, [open, existingEquipment, form]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset(defaultValues);
    }
    onOpenChange(newOpen);
  };

  // Create mutation (POST)
  const createMutation = useMutation({
    mutationFn: async (data: EquipmentFormData) => {
      const payload = buildPayload(data);
      const response = await apiRequest(
        "POST",
        `/api/crm/securities/${securityId}/equipment`,
        payload
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to create equipment details");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/securities"],
      });
      toast({ title: "Success", description: "Equipment details recorded" });
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update mutation (PATCH)
  const updateMutation = useMutation({
    mutationFn: async (data: EquipmentFormData) => {
      const payload = buildPayload(data);
      const response = await apiRequest(
        "PATCH",
        `/api/crm/securities/${securityId}/equipment`,
        payload
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to update equipment details");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/securities"],
      });
      toast({ title: "Success", description: "Equipment details updated" });
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (data: EquipmentFormData) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Equipment Details" : "Add Equipment Details"}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Equipment Type (required) */}
            <FormField
              control={form.control}
              name="equipmentType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Equipment Type *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-equipment-type">
                        <SelectValue placeholder="Select equipment type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {EQUIPMENT_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Make / Model row */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="make"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Make</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. Toyota"
                        data-testid="input-make"
                        {...field}
                        name="make"
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Model</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. 8FBN25"
                        data-testid="input-model"
                        {...field}
                        name="model"
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Year Manufactured / Serial Number row */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="yearManufactured"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Year Manufactured</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="e.g. 2020"
                        data-testid="input-year-manufactured"
                        {...field}
                        name="yearManufactured"
                        value={field.value ?? ""}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === "" ? null : parseInt(e.target.value, 10)
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="serialNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Serial Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. SN-12345"
                        data-testid="input-serial-number"
                        {...field}
                        name="serialNumber"
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Asset Tag / Condition row */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="assetTag"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Asset Tag</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. ASSET-001"
                        data-testid="input-asset-tag"
                        {...field}
                        name="assetTag"
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="condition"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Condition</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? "none"}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-condition">
                          <SelectValue placeholder="Select condition" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Not specified</SelectItem>
                        {CONDITION_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Hours Used / Weight row */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="hoursUsed"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Hours Used</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="e.g. 1500"
                        data-testid="input-hours-used"
                        {...field}
                        name="hoursUsed"
                        value={field.value ?? ""}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === "" ? null : parseInt(e.target.value, 10)
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="weightKg"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Weight (kg)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="e.g. 3500"
                        data-testid="input-weight-kg"
                        {...field}
                        name="weightKg"
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Capacity / Location row */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="capacity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Capacity</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. 2.5 tonne"
                        data-testid="input-capacity"
                        {...field}
                        name="capacity"
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="locationAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location Address</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. 123 Warehouse St"
                        data-testid="input-location-address"
                        {...field}
                        name="locationAddress"
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Additional details about the equipment"
                      data-testid="input-description"
                      {...field}
                      name="description"
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* isMobile toggle */}
            <FormField
              control={form.control}
              name="isMobile"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-3">
                  <FormLabel className="mb-0">Mobile Equipment</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      data-testid="switch-is-mobile"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isPending}
                data-testid="button-save"
              >
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEdit ? "Save Changes" : "Add Equipment"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Payload builder — converts empty strings to null for optional API fields
// ============================================================================
function buildPayload(data: EquipmentFormData) {
  return {
    equipmentType: data.equipmentType,
    serialNumber: data.serialNumber || null,
    assetTag: data.assetTag || null,
    make: data.make || null,
    model: data.model || null,
    yearManufactured: data.yearManufactured ?? null,
    description: data.description || null,
    condition: !data.condition || data.condition === "none" ? null : data.condition,
    locationAddress: data.locationAddress || null,
    isMobile: data.isMobile,
    hoursUsed: data.hoursUsed ?? null,
    weightKg: data.weightKg || null,
    capacity: data.capacity || null,
  };
}
